/**
 * # API Request Signing
 *
 * For some endpoints, we allow unauthenticated requests (e.g. on /i/id).
 * In the past, people have reverse engineered those API calls and spammed our server.
 * We cannot easily prevent this in general, as everything is in the client bundle.
 * But we add a signature to make it more effort to spoof.
 * We also don't give feedback on whether the signature was valid, making reverse engineering harder.
 * Not a security boundary: the key is public, so signed routes must still validate their input.
 *
 * Uses crypto-js for HMAC (works in both browsers and Node.js).
 * Follows Standard Webhooks spec: https://github.com/standard-webhooks/standard-webhooks
 */

import type { PostJsonRouteMapping } from "./routeUtils";
import { clientEnv } from "@/env.client";
import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";

/**
 * Routes that require request signing.
 * - Client (postJson): automatically adds signature headers
 * - Server (handlePostJson): automatically verifies and passes `signatureValid` to handler
 *
 * ⚠️ IMPORTANT: Handlers for signed routes MUST handle invalid signatures SILENTLY.
 * Do NOT return an error or different response - this would reveal to attackers
 * that their signature was incorrect. Instead, just return early or ignore the request.
 */
export const SIGNED_ROUTES = [
  "/api/track_error",
] as const satisfies readonly (keyof PostJsonRouteMapping)[];

export type SignedRoute = (typeof SIGNED_ROUTES)[number];

export function isSignedRoute(
  route: keyof PostJsonRouteMapping
): route is SignedRoute {
  return SIGNED_ROUTES.includes(route as SignedRoute);
}

export type SignatureHeaders = {
  "webhook-id": string;
  "webhook-timestamp": string;
  "webhook-signature": string;
};

const KEY_SUFFIX = "_ls47x"; // Non-standard suffix - makes reverse engineering harder
const MAX_TIMESTAMP_AGE_SECONDS = 5 * 60;

function getSigningKey(): string | null {
  const baseKey = clientEnv.NEXT_PUBLIC_API_SIGNATURE_KEY;
  return baseKey ? baseKey + KEY_SUFFIX : null;
}

function hmacSign(secret: string, message: string): string {
  return CryptoJS.HmacSHA256(message, secret).toString(CryptoJS.enc.Base64);
}

function timingSafeEqualStrings(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/** Generate signature headers for a request */
export function generateSignatureHeaders(
  body: string
): SignatureHeaders | null {
  const secret = getSigningKey();
  if (!secret) return null;

  try {
    const msgId = `msg_${uuidv4()}`;
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const signature = hmacSign(secret, `${msgId}.${timestamp}.${body}`);

    return {
      "webhook-id": msgId,
      "webhook-timestamp": timestamp,
      "webhook-signature": `v1,${signature}`,
    };
  } catch (e) {
    console.error("Error generating signature headers", e);
    return null;
  }
}

/** Verify a signed request */
export function verifyRequestSignature(
  body: string,
  headers: Record<string, string>
): boolean {
  const secret = getSigningKey();
  if (!secret) return true; // No key configured - skip verification

  try {
    const msgId = headers["webhook-id"];
    const timestamp = headers["webhook-timestamp"];
    const signature = headers["webhook-signature"];
    if (!msgId || !timestamp || !signature) return false;

    // Check timestamp is recent
    const ts = parseInt(timestamp, 10);
    if (
      isNaN(ts) ||
      Math.abs(Date.now() / 1000 - ts) > MAX_TIMESTAMP_AGE_SECONDS
    )
      return false;

    // Compute and compare signature
    const expected = hmacSign(secret, `${msgId}.${timestamp}.${body}`);
    const actual = signature.startsWith("v1,") ? signature.slice(3) : signature;
    return timingSafeEqualStrings(expected, actual);
  } catch {
    return false;
  }
}
